import React, { useState, useEffect, useRef } from "react";
import {
  Sparkles,
  X,
  MessageCircle,
  Send,
  Search,
  RefreshCw,
  Mic,
  MicOff,
  Undo2,
  Info,
  Edit3,
  PanelRightClose,
  PanelBottomClose,
  GripVertical,
  GripHorizontal,
} from "lucide-react";
import { aiService } from "../../../services/aiService";
import { useSpeechRecognition } from "../../../hooks/useSpeechRecognition";
import type {
  PromptTemplate,
  SimilarProcess,
  SimilarForm,
} from "../../../services/aiService";

export interface ChatMessage {
  id: string;
  type: "user" | "assistant" | "system";
  content: string;
  timestamp: number;
  prompt?: string;
  modifiedBpmn?: string;
}

interface AiChatSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  bpmnXml: string;
  formJson?: string;
  onBpmnModified?: (xml: string) => void;
  onFormModified?: (json: string) => void;
}

const AiChatSidebar: React.FC<AiChatSidebarProps> = ({
  isOpen,
  onClose,
  bpmnXml,
  formJson = "",
  onBpmnModified,
  onFormModified,
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPrompts, setShowPrompts] = useState(true);
  const [promptTemplates, setPromptTemplates] = useState<PromptTemplate[]>([]);
  
  // Panel positioning and sizing
  const [panelPosition, setPanelPosition] = useState<'right' | 'bottom'>('right');
  const [panelWidth, setPanelWidth] = useState(480);
  const [panelHeight, setPanelHeight] = useState(480);
  const [isResizing, setIsResizing] = useState(false);
  const resizeStartRef = useRef<{ x: number; y: number; width: number; height: number } | null>(null);
  
  // AI Mode: 'analyze' or 'modify'
  const [aiMode, setAiMode] = useState<'analyze' | 'modify'>('modify');
  
  // Undo history for BPMN and Form
  const [bpmnHistory, setBpmnHistory] = useState<string[]>([]);
  const [formHistory, setFormHistory] = useState<string[]>([]);

  // Ref to auto-scroll messages
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Similar Reference State
  const [enableSimilarProcessReference, setEnableSimilarProcessReference] =
    useState(false);
  const [enableSimilarFormReference, setEnableSimilarFormReference] =
    useState(false);

  const [showSimilarProcessPanel, setShowSimilarProcessPanel] = useState(false);
  const [showSimilarFormPanel, setShowSimilarFormPanel] = useState(false);

  const [similarProcesses, setSimilarProcesses] = useState<SimilarProcess[]>(
    [],
  );
  const [similarForms, setSimilarForms] = useState<SimilarForm[]>([]);

  const [selectedSimilarProcesses, setSelectedSimilarProcesses] = useState<
    SimilarProcess[]
  >([]);
  const [selectedSimilarForms, setSelectedSimilarForms] = useState<
    SimilarForm[]
  >([]);

  const [isLoadingSimilarProcesses, setIsLoadingSimilarProcesses] =
    useState(false);
  const [isLoadingSimilarForms, setIsLoadingSimilarForms] = useState(false);

  const [processSearchTerm, setProcessSearchTerm] = useState("");
  const [formSearchTerm, setFormSearchTerm] = useState("");

  const [processSearchResults, setProcessSearchResults] = useState<
    SimilarProcess[]
  >([]);
  const [formSearchResults, setFormSearchResults] = useState<SimilarForm[]>([]);

  const [storedMessage, setStoredMessage] = useState("");

  // Context Detection - Prioritize Form context if we have form JSON
  const isFormContext = !!(formJson && formJson.trim().length > 0);
  const isProcessContext =
    !isFormContext && !!(bpmnXml && bpmnXml.trim().length > 0);
  const hasNoContext = !isFormContext && !isProcessContext;

  // Theme colors - unified to the "workflow" design as requested
  const primaryBg = "bg-indigo-600";
  const primaryText = "text-indigo-600";
  const primaryLightBg = "bg-indigo-100";
  const primaryLightBorder = "border-indigo-200";
  const primaryHoverBg = "hover:bg-indigo-700";

  const primaryToggleBg = "bg-indigo-500";

  const placeholderText = isFormContext
    ? "Ask me to analyze or modify your Form.io form..."
    : isProcessContext
      ? "Ask me to analyze or modify your BPMN process..."
      : "Ask me to help with your BPMN process or Form.io form...";

  const contextPromptTemplates = isFormContext
    ? promptTemplates.filter((t) =>
        t.title.toLowerCase().match(/form|field|layout|validation|contact/),
      )
    : isProcessContext
      ? promptTemplates.filter(
          (t) =>
            !t.title
              .toLowerCase()
              .match(/form|field|layout|validation|contact/),
        )
      : promptTemplates;

  const initializedRaf = useRef(false);

  useEffect(() => {
    if (isOpen) {
      if (messages.length === 0 && !initializedRaf.current) {
        initializedRaf.current = true;
        loadPromptTemplates();
        updateContextMessage();
        checkAIStatus();
      }
    } else {
      // Reset initialization when drawer closes, so opening it again properly shows messages
      initializedRaf.current = false;
    }
  }, [isOpen, messages.length]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  const generateMessageId = () =>
    `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  const addSystemMessage = (content: string) => {
    setMessages((prev) => [
      ...prev,
      {
        id: generateMessageId(),
        type: "system",
        content,
        timestamp: Date.now(),
      },
    ]);
  };

  const addUserMessage = (content: string) => {
    setMessages((prev) => [
      ...prev,
      { id: generateMessageId(), type: "user", content, timestamp: Date.now() },
    ]);
  };

  const addAssistantMessage = (content: string, originalPrompt?: string) => {
    setMessages((prev) => [
      ...prev,
      {
        id: generateMessageId(),
        type: "assistant",
        content,
        timestamp: Date.now(),
        prompt: originalPrompt,
      },
    ]);
  };

  const updateContextMessage = () => {
    if (isFormContext) {
      addSystemMessage(
        "AI Assistant is ready! I can help you analyze and modify your Form.io form.",
      );
    } else if (isProcessContext) {
      addSystemMessage(
        "AI Assistant is ready! I can help you analyze and modify your BPMN process.",
      );
    } else {
      addSystemMessage(
        "AI Assistant is ready! Please load a BPMN process or Form.io form to get started.",
      );
    }
  };

  const loadPromptTemplates = async () => {
    try {
      const response = await aiService.getSuggestedPrompts();
      if (response && response.prompts) setPromptTemplates(response.prompts);
    } catch {
      addSystemMessage(
        "Unable to load prompt suggestions. You can still type your own requests.",
      );
    }
  };

  const checkAIStatus = async () => {
    try {
      const response = await aiService.getStatus();
      if (!response.enabled)
        addSystemMessage(
          "AI service is currently unavailable. Please try again later.",
        );
    } catch {
      addSystemMessage(
        "Unable to connect to AI service. Please check if the backend service is running.",
      );
    }
  };

  const clearChat = () => {
    setMessages([]);
    addSystemMessage("Chat cleared. AI Assistant is ready to help!");
    setShowPrompts(true);
  };

  const usePromptTemplate = (template: PromptTemplate) => {
    setInputMessage(template.template);
    setShowPrompts(false);
  };

  // Speech recognition - initialized after addSystemMessage is defined
  const {
    transcript,
    isListening,
    recordingState,
    isSupported: isSpeechSupported,
    error: speechError,
    startListening,
    stopListening,
    resetTranscript,
  } = useSpeechRecognition({
    lang: 'en-US',
    continuous: false,
    interimResults: false,
    maxDuration: 100,
    onError: (error) => {
      addSystemMessage(`Speech recognition error: ${error}`);
    },
  });

  const toggleSimilarProcessReference = () => {
    setEnableSimilarProcessReference(!enableSimilarProcessReference);
    if (enableSimilarProcessReference) {
      setShowSimilarProcessPanel(false);
      setSimilarProcesses([]);
      setSelectedSimilarProcesses([]);
    }
  };

  const handleUndo = () => {
    if (isFormContext && formHistory.length > 0) {
      const previousState = formHistory[formHistory.length - 1];
      setFormHistory(prev => prev.slice(0, -1));
      onFormModified?.(previousState);
      addSystemMessage("Undo successful. Form reverted to previous state.");
    } else if (isProcessContext && bpmnHistory.length > 0) {
      const previousState = bpmnHistory[bpmnHistory.length - 1];
      setBpmnHistory(prev => prev.slice(0, -1));
      onBpmnModified?.(previousState);
      addSystemMessage("Undo successful. BPMN diagram reverted to previous state.");
    } else {
      addSystemMessage("No changes to undo.");
    }
  };

  // Chat Execution Logic
  // Update input message when transcript changes
  useEffect(() => {
    if (transcript) {
      setInputMessage((prev) => prev + (prev ? ' ' : '') + transcript);
      resetTranscript();
    }
  }, [transcript, resetTranscript]);

  // Handle microphone toggle
  const handleMicrophoneToggle = () => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  const handleSendMessage = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const msg = inputMessage.trim();
    if (!msg || isLoading) return;

    addUserMessage(msg);
    setInputMessage("");
    setShowPrompts(false);

    // Use the selected AI mode instead of auto-detecting
    if (aiMode === 'analyze') {
      // Force analysis mode
      if (isFormContext) {
        handleFormAnalysisRequest(msg);
      } else if (isProcessContext) {
        handleAnalysisRequest(msg);
      } else {
        handleGeneralRequest(msg);
      }
    } else {
      // Force modify mode
      if (isFormContext) {
        if (enableSimilarFormReference && selectedSimilarForms.length === 0) {
          setStoredMessage(msg);
          findSimilarForms();
        } else if (
          enableSimilarFormReference &&
          selectedSimilarForms.length > 0
        ) {
          handleFormModificationWithContext(msg);
        } else {
          handleFormModificationRequest(msg);
        }
      } else if (isProcessContext) {
        if (
          enableSimilarProcessReference &&
          selectedSimilarProcesses.length === 0
        ) {
          setStoredMessage(msg);
          findSimilarProcesses();
        } else if (
          enableSimilarProcessReference &&
          selectedSimilarProcesses.length > 0
        ) {
          handleModificationWithContext(msg);
        } else {
          handleModificationRequest(msg);
        }
      } else {
        handleGeneralRequest(msg);
      }
    }
  };

  // API Call Handlers (Process)
  const handleModificationRequest = async (message: string) => {
    if (!aiService.validateBPMN(bpmnXml)) {
      addSystemMessage(
        "Please ensure you have a valid BPMN diagram loaded before requesting modifications.",
      );
      return;
    }

    // Save current state to history before modification
    setBpmnHistory(prev => [...prev, bpmnXml]);

    setIsLoading(true);
    addSystemMessage("Processing your modification request...");
    try {
      const response = await aiService.modifyBPMN(bpmnXml, `${message} Make sure BPMN alignment is good. check twice.`);
      if (response && response.modifiedBpmn) {
        //addAssistantMessage(response.modifiedBpmn, message);
        onBpmnModified?.(response.modifiedBpmn);
        addSystemMessage(
          "BPMN diagram has been updated with the AI-generated changes.",
        );
      } else {
        // Remove from history if modification failed
        setBpmnHistory(prev => prev.slice(0, -1));
        addSystemMessage(
          "Failed to modify BPMN. Please try again with a more specific request.",
        );
      }
    } catch (e: any) {
      // Remove from history if modification failed
      setBpmnHistory(prev => prev.slice(0, -1));
      
      // Provide more specific error messages
      let errorMessage = "Failed to modify BPMN. ";
      if (e.message?.includes("not well-formed") || e.message?.includes("invalid BPMN XML")) {
        errorMessage += "The AI generated invalid XML. Please try:\n" +
          "• Simplifying your request\n" +
          "• Breaking it into smaller changes\n" +
          "• Being more specific about what to modify";
      } else if (e.message?.includes("timeout") || e.message?.includes("network")) {
        errorMessage += "Network error. Please check your connection and try again.";
      } else {
        errorMessage += e.message || "Unknown error occurred.";
      }
      
      addSystemMessage(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAnalysisRequest = async (message: string) => {
    if (!aiService.validateBPMN(bpmnXml)) {
      addSystemMessage(
        "Please ensure you have a valid BPMN diagram loaded before requesting analysis.",
      );
      return;
    }
    setIsLoading(true);
    addSystemMessage("Analyzing your BPMN process...");
    try {
      const response = await aiService.analyzeBPMN(bpmnXml);
      if (response && response.analysis)
        addAssistantMessage(response.analysis, message);
      else addSystemMessage("Failed to analyze BPMN. Please try again.");
    } catch (e: any) {
      addSystemMessage(`Error: ${e.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Similar Process Logic
  const findSimilarProcesses = async () => {
    if (!bpmnXml) {
      addSystemMessage(
        "Please ensure you have a valid BPMN diagram loaded before finding similar processes.",
      );
      return;
    }
    setIsLoadingSimilarProcesses(true);
    try {
      const processName = aiService.extractProcessName(bpmnXml);
      const response = await aiService.findSimilarProcesses(processName, 1);
      if (
        response.success &&
        response.processes &&
        response.processes.length > 0
      ) {
        setSimilarProcesses(
          response.processes.map((p: any) => ({ ...p, approved: false })),
        );
        setShowSimilarProcessPanel(true);
        addSystemMessage(
          `Found ${response.processes.length} similar process(es). Review and approve the ones you want to use as reference.`,
        );
      } else {
        addSystemMessage(
          "No similar processes found. You can search manually for processes to use as reference.",
        );
      }
    } catch {
      addSystemMessage(
        "Error finding similar processes. You can search manually for processes to use as reference.",
      );
    } finally {
      setIsLoadingSimilarProcesses(false);
    }
  };

  const searchProcesses = async () => {
    if (processSearchTerm.trim().length < 2) {
      setProcessSearchResults([]);
      return;
    }
    try {
      const resp = await aiService.findSimilarProcesses(processSearchTerm, 10);
      if (resp.success && resp.processes)
        setProcessSearchResults(resp.processes);
    } catch {
      setProcessSearchResults([]);
    }
  };

  const submitWithSimilarProcesses = () => {
    if (selectedSimilarProcesses.length === 0) {
      addSystemMessage(
        "Please select at least one similar process to use as reference.",
      );
      return;
    }
    setShowSimilarProcessPanel(false);
    addSystemMessage(
      `Using ${selectedSimilarProcesses.length} similar processes as reference for your request.`,
    );
    if (storedMessage) {
      handleModificationWithContext(storedMessage);
      setStoredMessage("");
    }
  };

  const handleModificationWithContext = async (message: string) => {
    if (!aiService.validateBPMN(bpmnXml)) {
      addSystemMessage(
        "Please ensure you have a valid BPMN diagram loaded before requesting modifications.",
      );
      return;
    }

    // Save current state to history before modification
    setBpmnHistory(prev => [...prev, bpmnXml]);

    setIsLoading(true);
    addSystemMessage(
      "Processing your modification request with similar process context...",
    );
    try {
      const xmls: string[] = [];
      for (const p of selectedSimilarProcesses) {
        const content = await aiService.getProcessContent(p.nameSlug);
        if (content?.bpmn) xmls.push(content.bpmn);
      }
      const response = await aiService.modifyBPMNWithContext(
        bpmnXml,
        `${message} Please ensure the BPMN diagram is well-aligned and logically structured. Double-check the output for any potential alignment issues.`,
        xmls,
      );
      if (response && response.modifiedBpmn) {
        //addAssistantMessage(response.modifiedBpmn, message);
        onBpmnModified?.(response.modifiedBpmn);
        addSystemMessage(
          "BPMN diagram has been updated with the AI-generated changes.",
        );
      } else {
        // Remove from history if modification failed
        setBpmnHistory(prev => prev.slice(0, -1));
        addSystemMessage("Failed to modify BPMN with context.");
      }
    } catch (e: any) {
      // Remove from history if modification failed
      setBpmnHistory(prev => prev.slice(0, -1));
      addSystemMessage(
        `Error processing modification request with context: ${e.message}`,
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleGeneralRequest = (message: string) => {
    handleModificationRequest(message);
  };

  // Form AI Methods
  const getFormName = () => {
    if (!formJson) return "Untitled Process";
    try {
      const formObj = JSON.parse(formJson);
      return formObj.name || formObj.title || "Untitled Process";
    } catch {
      return "Untitled Process";
    }
  };

  const handleFormModificationRequest = async (msg: string) => {
    if (!formJson || formJson.trim().length === 0) {
      addSystemMessage(
        "Please ensure you have a valid form loaded before requesting modifications.",
      );
      return;
    }

    // Save current state to history before modification
    setFormHistory(prev => [...prev, formJson]);

    setIsLoading(true);
    addSystemMessage("Processing your form modification request...");
    try {
      const response = await aiService.modifyForm(formJson, msg);
      if (response && response.modifiedForm) {
        onFormModified?.(response.modifiedForm);
        addAssistantMessage(
          `I've successfully modified your form based on your request: "${msg}". The form has been updated with the new structure.`,
          msg,
        );
        addSystemMessage("Form has been updated in the form builder above.");
      } else {
        // Remove from history if modification failed
        setFormHistory(prev => prev.slice(0, -1));
        addSystemMessage(response.error || "Failed to modify form.");
      }
    } catch (e: any) {
      // Remove from history if modification failed
      setFormHistory(prev => prev.slice(0, -1));
      addSystemMessage(
        `Error processing form modification request: ${e.message}`,
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormAnalysisRequest = async (msg: string) => {
    if (!formJson || formJson.trim().length === 0) {
      addSystemMessage(
        "Please ensure you have a valid form loaded before requesting analysis.",
      );
      return;
    }
    setIsLoading(true);
    addSystemMessage("Analyzing your form...");
    try {
      const response = await aiService.analyzeForm(formJson);
      if (response && response.analysis) {
        addAssistantMessage(response.analysis, msg);
      } else {
        addSystemMessage(response.error || "Failed to analyze form.");
      }
    } catch (e: any) {
      addSystemMessage(`Error processing form analysis request: ${e.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleSimilarFormReference = () => {
    setEnableSimilarFormReference(!enableSimilarFormReference);
    if (enableSimilarFormReference) {
      setShowSimilarFormPanel(false);
      setSimilarForms([]);
      setSelectedSimilarForms([]);
    }
  };

  const findSimilarForms = async () => {
    if (!formJson) {
      addSystemMessage(
        "Please ensure you have a valid form loaded before finding similar forms.",
      );
      return;
    }
    setIsLoadingSimilarForms(true);
    try {
      const resp = await aiService.findSimilarForms(getFormName(), 1);
      if (resp.success && resp.forms && resp.forms.length > 0) {
        setSimilarForms(
          resp.forms.map((f: any) => ({ ...f, approved: false })),
        );
        setShowSimilarFormPanel(true);
        addSystemMessage(
          `Found ${resp.forms.length} similar form(s). Review and approve the ones you want to use as reference.`,
        );
      } else {
        addSystemMessage(
          "No similar forms found. You can search manually for forms to use as reference.",
        );
      }
    } catch {
      addSystemMessage(
        "Error finding similar forms. You can search manually for forms to use as reference.",
      );
    } finally {
      setIsLoadingSimilarForms(false);
    }
  };

  const searchForms = async () => {
    if (formSearchTerm.trim().length < 2) {
      setFormSearchResults([]);
      return;
    }
    try {
      const resp = await aiService.findSimilarForms(formSearchTerm, 10);
      if (resp.success && resp.forms) setFormSearchResults(resp.forms);
    } catch {
      setFormSearchResults([]);
    }
  };

  const submitWithSimilarForms = () => {
    if (selectedSimilarForms.length === 0) {
      addSystemMessage(
        "Please select at least one similar form to use as reference.",
      );
      return;
    }
    setShowSimilarFormPanel(false);
    addSystemMessage(
      `Using ${selectedSimilarForms.length} similar forms as reference for your request.`,
    );
    if (storedMessage) {
      handleFormModificationWithContext(storedMessage);
      setStoredMessage("");
    }
  };

  const handleFormModificationWithContext = async (msg: string) => {
    if (!formJson || formJson.trim().length === 0) {
      addSystemMessage(
        "Please ensure you have a valid form loaded before requesting modifications.",
      );
      return;
    }

    // Save current state to history before modification
    setFormHistory(prev => [...prev, formJson]);

    setIsLoading(true);
    addSystemMessage(
      "Processing your form modification request with similar form context...",
    );
    try {
      const formJsons: string[] = [];
      for (const f of selectedSimilarForms) {
        const content = await aiService.getFormContent(f.nameSlug);
        // API returns FormContent with formioJson (base64); decode to JSON string for modifyFormWithContext
        if (content?.formioJson) {
          try {
            const decoded = atob(content.formioJson);
            formJsons.push(decoded);
          } catch (e) {
            console.warn("Failed to decode form content for", f.nameSlug, e);
          }
        }
      }
      const response = await aiService.modifyFormWithContext(
        formJson,
        msg,
        formJsons,
      );
      if (response && response.modifiedForm) {
        onFormModified?.(response.modifiedForm);
        addAssistantMessage(
          `I've successfully modified your form using ${selectedSimilarForms.length} similar forms as reference. Your request: "${msg}" has been applied.`,
          msg,
        );
        addSystemMessage(
          "Form has been updated in the form builder above using similar form patterns.",
        );
      } else {
        // Remove from history if modification failed
        setFormHistory(prev => prev.slice(0, -1));
        addSystemMessage(
          response.error || "Failed to modify form with context.",
        );
      }
    } catch (e: any) {
      // Remove from history if modification failed
      setFormHistory(prev => prev.slice(0, -1));
      addSystemMessage(
        `Error processing form modification request with context: ${e.message}`,
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Panel positioning and resizing handlers
  const togglePanelPosition = () => {
    setPanelPosition(prev => prev === 'right' ? 'bottom' : 'right');
  };

  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    resizeStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      width: panelWidth,
      height: panelHeight,
    };
  };

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!resizeStartRef.current) return;

      if (panelPosition === 'right') {
        const deltaX = resizeStartRef.current.x - e.clientX;
        const newWidth = Math.max(300, Math.min(800, resizeStartRef.current.width + deltaX));
        setPanelWidth(newWidth);
      } else {
        const deltaY = resizeStartRef.current.y - e.clientY;
        const newHeight = Math.max(300, Math.min(800, resizeStartRef.current.height + deltaY));
        setPanelHeight(newHeight);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      resizeStartRef.current = null;
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, panelPosition]);

  return (
    <div
      className={`fixed bg-slate-50 shadow-2xl transition-all duration-500 ease-[cubic-bezier(0.25,0.8,0.25,1)] z-[9999] flex flex-col ${
        panelPosition === 'right'
          ? `top-0 right-0 h-screen ${isOpen ? "translate-x-0 opacity-100 border-l border-gray-200" : "translate-x-full opacity-0"}`
          : `bottom-0 left-0 right-0 ${isOpen ? "translate-y-0 opacity-100 border-t border-gray-200" : "translate-y-full opacity-0"}`
      }`}
      style={panelPosition === 'right' ? { width: `${panelWidth}px` } : { height: `${panelHeight}px` }}
    >
      {/* Resize Handle */}
      {isOpen && (
        <div
          onMouseDown={handleResizeStart}
          className={`absolute ${
            panelPosition === 'right'
              ? 'left-0 top-0 bottom-0 w-1 hover:w-1.5 cursor-ew-resize'
              : 'left-0 right-0 top-0 h-1 hover:h-1.5 cursor-ns-resize'
          } bg-transparent hover:bg-indigo-400 transition-all z-50 group`}
        >
          <div className={`absolute ${
            panelPosition === 'right'
              ? 'left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2'
              : 'left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2'
          } opacity-0 group-hover:opacity-100 transition-opacity bg-indigo-500 rounded-full p-1`}>
            {panelPosition === 'right' ? <GripVertical size={12} className="text-white" /> : <GripHorizontal size={12} className="text-white" />}
          </div>
        </div>
      )}
      {/* Header Matches Angular template exactly word-for-word */}
      <div className="px-4 py-2.5 border-b border-gray-200 flex justify-between items-center bg-white shadow-sm z-10">
        <div className="flex items-center gap-3">
          <div
            className={`p-2 ${primaryLightBg} ${primaryText} rounded-lg flex items-center justify-center h-9 w-9`}
          >
            <Sparkles size={18} />
          </div>
          <div>
            <div className="font-semibold text-[14px] text-gray-800 leading-tight">
              {isFormContext ? "AI Form Assistant" : "AI Process Assistant"}
            </div>
            <div className="text-[11px] text-gray-500 font-medium leading-tight truncate max-w-[180px]">
              {isFormContext
                ? getFormName()
                : aiService.extractProcessName(bpmnXml)}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex bg-gray-100 rounded-lg p-0.5 gap-0.5">
            <button
              onClick={() => setAiMode('analyze')}
              title="Analyze Mode"
              className={`px-2 py-1 rounded-md text-[11px] font-semibold transition-all duration-200 flex items-center gap-1 ${
                aiMode === 'analyze'
                  ? 'bg-white text-indigo-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <Info size={12} />
              Analyze
            </button>
            <button
              onClick={() => setAiMode('modify')}
              title="Modify Mode"
              className={`px-2 py-1 rounded-md text-[11px] font-semibold transition-all duration-200 flex items-center gap-1 ${
                aiMode === 'modify'
                  ? 'bg-white text-indigo-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <Edit3 size={12} />
              Modify
            </button>
          </div>
          <div className="w-px h-5 bg-gray-300"></div>
          <button
            onClick={togglePanelPosition}
            title={panelPosition === 'right' ? 'Move to Bottom' : 'Move to Right'}
            className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors duration-200 flex justify-center items-center"
          >
            {panelPosition === 'right' ? <PanelBottomClose size={16} /> : <PanelRightClose size={16} />}
          </button>
          <button
            onClick={handleUndo}
            disabled={(isFormContext && formHistory.length === 0) || (isProcessContext && bpmnHistory.length === 0)}
            title="Undo Last Change"
            className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors duration-200 flex justify-center items-center disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:text-gray-400 disabled:hover:bg-transparent"
          >
            <Undo2 size={16} />
          </button>
          <button
            onClick={clearChat}
            title="Clear Chat"
            className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors duration-200 flex justify-center items-center"
          >
            <RefreshCw size={16} />
          </button>
          <button
            onClick={onClose}
            title="Close"
            className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors duration-200 flex justify-center items-center"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Similar Process and Form Toggles */}
      {(isProcessContext || isFormContext) && (
        <div className="px-4 py-2 bg-white border-b border-gray-100 shadow-sm z-10 space-y-3">
          {isProcessContext && (
            <div
              className="flex !flex-row items-center justify-between gap-4 flex-nowrap cursor-pointer group"
              onClick={toggleSimilarProcessReference}
            >
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-gray-800 block text-[13.5px] leading-tight truncate">
                  Enable Similar Process Reference
                </div>
                <div className="text-[10.5px] text-gray-500 block mt-0.5 group-hover:text-gray-600 transition-colors leading-tight line-clamp-1">
                  Use existing processes as reference for AI modifications
                </div>
              </div>
              <div
                className={`w-10 h-5 flex-shrink-0 rounded-full relative transition-colors duration-300 ${enableSimilarProcessReference ? primaryToggleBg : "bg-gray-200 group-hover:bg-gray-300"}`}
              >
                <div
                  className={`absolute w-3.5 h-3.5 rounded-full bg-white top-[3px] transition-transform duration-300 shadow-sm ${enableSimilarProcessReference ? "translate-x-[22px]" : "translate-x-[3px]"}`}
                />
              </div>
            </div>
          )}

          {isFormContext && (
            <div
              className="flex !flex-row items-center justify-between gap-4 flex-nowrap cursor-pointer group"
              onClick={toggleSimilarFormReference}
            >
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-gray-800 block text-[13.5px] leading-tight truncate">
                  Enable Similar Form Reference
                </div>
                <div className="text-[10.5px] text-gray-500 block mt-0.5 group-hover:text-gray-600 transition-colors leading-tight line-clamp-1">
                  Use existing forms as reference for AI modifications
                </div>
              </div>
              <div
                className={`w-10 h-5 flex-shrink-0 rounded-full relative transition-colors duration-300 ${enableSimilarFormReference ? primaryToggleBg : "bg-gray-200 group-hover:bg-gray-300"}`}
              >
                <div
                  className={`absolute w-3.5 h-3.5 rounded-full bg-white top-[3px] transition-transform duration-300 shadow-sm ${enableSimilarFormReference ? "translate-x-[22px]" : "translate-x-[3px]"}`}
                />
              </div>
            </div>
          )}
        </div>
      )}

      <div
        className="flex-1 overflow-y-auto p-4 space-y-4 relative"
        style={{ scrollBehavior: "smooth" }}
      >
        {/* Prompts list */}
        {showPrompts && contextPromptTemplates.length > 0 && (
          <div className="mb-4">
            <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5 ml-0.5">
              Suggested Actions
            </div>
            <div className={`grid gap-1.5 ${panelPosition === 'right' ? 'grid-cols-2' : 'grid-cols-3'}`}>
              {contextPromptTemplates.map((template) => (
                <div
                  key={template.id}
                  onClick={() => usePromptTemplate(template)}
                  className="flex items-center gap-2 px-2.5 py-1.5 bg-white border border-gray-200 rounded-lg hover:!border-indigo-400 hover:!bg-indigo-50 cursor-pointer transition-all duration-200 group"
                >
                  <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0 group-hover:bg-white transition-colors duration-200">
                    <MessageCircle size={12} className="text-indigo-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-gray-800 text-[11.5px] truncate">
                      {template.title}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Message Loop */}
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`flex ${msg.type === "user" ? "justify-end" : "justify-start"} w-full`}
          >
            {msg.type === "system" && (
              <div className="w-full text-center my-3 relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200"></div>
                </div>
                <div className="relative flex justify-center">
                  <span className="text-[11px] font-medium bg-gray-100 text-gray-500 px-3 py-1 rounded-full border border-gray-200 shadow-sm">
                    {msg.content}
                  </span>
                </div>
              </div>
            )}
            {msg.type !== "system" && (
              <div
                className={`flex gap-2 max-w-[85%] ${msg.type === "user" ? "flex-row-reverse" : ""}`}
              >
                <div
                  className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-1 shadow-sm border ${msg.type === "user" ? `${primaryLightBg} ${primaryText} ${primaryLightBorder}` : "bg-green-100 text-green-600 border-green-200"}`}
                >
                  {msg.type === "user" ? (
                    <MessageCircle size={14} />
                  ) : (
                    <Sparkles size={14} />
                  )}
                </div>
                <div>
                  <div
                    className={`p-3 rounded-2xl shadow-sm border ${msg.type === "user" ? `${primaryBg} text-white border-transparent rounded-tr-sm` : "bg-white text-gray-800 border-gray-200 rounded-tl-sm"}`}
                  >
                    <div className="text-[13.5px] whitespace-pre-wrap leading-relaxed">
                      {msg.content}
                    </div>
                  </div>
                  <div
                    className={`text-[10px] text-gray-400 mt-1.5 font-medium ${msg.type === "user" ? "text-right pr-1" : "text-left pl-1"}`}
                  >
                    {new Date(msg.timestamp).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
        {isLoading && (
          <div className="flex justify-start gap-2 max-w-[85%] mt-2">
            <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 border bg-green-100 text-green-600 border-green-200 shadow-sm">
              <Sparkles size={14} />
            </div>
            <div className="px-4 py-3.5 bg-white rounded-2xl rounded-tl-sm shadow-sm border border-gray-200 flex items-center gap-1.5 h-10">
              <div
                className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"
                style={{ animationDelay: "0ms" }}
              />
              <div
                className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"
                style={{ animationDelay: "150ms" }}
              />
              <div
                className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"
                style={{ animationDelay: "300ms" }}
              />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} className="h-2" />
      </div>

      {/* Similar Process Modal Overlay */}
      {showSimilarProcessPanel && (
        <div className="absolute inset-0 bg-white/98 backdrop-blur-md z-20 flex flex-col shadow-[-10px_0_20px_rgba(0,0,0,0.05)] border-l border-gray-200">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-white">
            <div className="font-semibold text-gray-800 text-[13.5px]">
              Similar Process Reference
            </div>
            <button
              onClick={() => setShowSimilarProcessPanel(false)}
              className="text-gray-400 hover:text-gray-700 hover:bg-gray-100 p-1.5 rounded transition-colors"
            >
              <X size={16} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-5 flex flex-col gap-5">
            {isLoadingSimilarProcesses ? (
              <div className="text-center text-gray-500 py-10 flex flex-col items-center justify-center gap-3">
                <RefreshCw
                  size={24}
                  className="animate-spin text-indigo-400 mx-auto"
                />
                <p className="text-sm font-medium">
                  Scanning database for similar processes...
                </p>
              </div>
            ) : (
              <>
                {similarProcesses.length > 0 ? (
                  <div>
                    <p className="text-[13px] font-medium text-gray-600 mb-3 block">
                      Matches found automatically:
                    </p>
                    <div className="flex flex-col gap-2.5">
                      {similarProcesses.map((p) => (
                        <div
                          key={p.id}
                          className={`p-3.5 border rounded-xl transition-all duration-200 ${p.approved ? "border-indigo-500 bg-indigo-50/50 shadow-sm" : "border-gray-200 bg-white hover:border-gray-300"}`}
                        >
                          <div className="flex justify-between items-start gap-4">
                            <div>
                              <div className="font-semibold text-[13px] text-gray-800 leading-tight">
                                {p.name}
                              </div>
                              <div className="text-[12px] text-gray-500 mt-1 line-clamp-2">
                                {p.description ||
                                  "No description available for this process model."}
                              </div>
                            </div>
                            <button
                              onClick={() => {
                                const updated = similarProcesses.map((sim) =>
                                  sim.id === p.id
                                    ? { ...sim, approved: !sim.approved }
                                    : sim,
                                );
                                setSimilarProcesses(updated);
                                setSelectedSimilarProcesses(
                                  updated.filter((sim) => sim.approved),
                                );
                              }}
                              className={`flex-shrink-0 text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full transition-all duration-200 ${p.approved ? "bg-indigo-600 text-white border border-transparent shadow-sm" : "bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100 hover:text-gray-800"}`}
                            >
                              {p.approved ? "✓ Selected" : "Select"}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-[13px] text-gray-500 bg-orange-50/50 p-4 rounded-xl border border-orange-100 text-center flex flex-col items-center justify-center gap-2">
                    <Search size={20} className="text-orange-300" />
                    <p>
                      No similar processes found automatically.
                      <br />
                      Try searching manually below.
                    </p>
                  </div>
                )}

                <div className="border-t border-gray-100 pt-5">
                  <h6 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-2.5">
                    Manual Directory Search
                  </h6>
                  <div className="flex gap-2 mb-3">
                    <div className="relative flex-1">
                      <input
                        type="text"
                        value={processSearchTerm}
                        onChange={(e) => setProcessSearchTerm(e.target.value)}
                        onKeyDown={(e) =>
                          e.key === "Enter" && searchProcesses()
                        }
                        placeholder="Search processes by name..."
                        className="w-full text-sm border border-gray-300 rounded-lg pl-3 pr-10 py-2 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none transition-all"
                      />
                      <button
                        onClick={searchProcesses}
                        className="absolute right-1 top-1 bottom-1 px-2.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors flex items-center justify-center"
                      >
                        <Search size={16} />
                      </button>
                    </div>
                  </div>
                  {processSearchResults.length > 0 && (
                    <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-xl bg-white shadow-sm ring-1 ring-black ring-opacity-5">
                      {processSearchResults.map((res) => (
                        <div
                          key={res.id}
                          className="px-3.5 py-2.5 border-b border-gray-100 last:border-0 hover:bg-indigo-50 cursor-pointer flex justify-between items-center group transition-colors"
                          onClick={() => {
                            if (
                              selectedSimilarProcesses.length < 3 &&
                              !selectedSimilarProcesses.find(
                                (s) => s.id === res.id,
                              )
                            ) {
                              setSelectedSimilarProcesses([
                                ...selectedSimilarProcesses,
                                { ...res, approved: true },
                              ]);
                              setProcessSearchTerm("");
                              setProcessSearchResults([]);
                            }
                          }}
                        >
                          <div>
                            <span className="font-medium text-gray-700 text-sm block">
                              {res.name}
                            </span>
                            {res.description && (
                              <span className="text-[11px] text-gray-400 block line-clamp-1 truncate pr-4">
                                {res.description}
                              </span>
                            )}
                          </div>
                          <span className="text-[11px] font-bold uppercase tracking-wider bg-white border border-gray-200 text-gray-500 px-2 py-1 rounded shadow-sm opacity-0 group-hover:opacity-100 group-hover:border-indigo-200 group-hover:text-indigo-600 transition-all flex-shrink-0">
                            Add +
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {selectedSimilarProcesses.length > 0 && (
                  <div className="bg-indigo-50/50 border border-indigo-100 rounded-xl p-3.5 shadow-sm mt-auto">
                    <div className="text-[10.5px] font-semibold text-indigo-800 uppercase tracking-wider mb-2.5 flex items-center justify-between">
                      <span>Selected References</span>
                      <span className="bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">
                        {selectedSimilarProcesses.length} / 3
                      </span>
                    </div>
                    <div className="flex flex-col gap-2">
                      {selectedSimilarProcesses.map((p) => (
                        <div
                          key={p.id}
                          className="flex justify-between items-center text-[13px] bg-white px-3 py-2 rounded-lg border border-indigo-100 shadow-sm group"
                        >
                          <span className="text-gray-700 truncate font-medium">
                            {p.name}
                          </span>
                          <button
                            onClick={() => {
                              setSelectedSimilarProcesses(
                                selectedSimilarProcesses.filter(
                                  (s) => s.id !== p.id,
                                ),
                              );
                              setSimilarProcesses(
                                similarProcesses.map((sim) =>
                                  sim.id === p.id
                                    ? { ...sim, approved: false }
                                    : sim,
                                ),
                              );
                            }}
                            className="text-gray-400 hover:text-red-500 hover:bg-red-50 p-1 rounded transition-colors"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="p-4 border-t border-gray-200 bg-white flex gap-3 justify-end shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.02)]">
            <button
              onClick={() => setShowSimilarProcessPanel(false)}
              className="px-5 py-2 text-sm font-semibold text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-800 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={submitWithSimilarProcesses}
              disabled={selectedSimilarProcesses.length === 0}
              className="px-5 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:bg-gray-400 transition-colors shadow-sm whitespace-nowrap"
            >
              Use {selectedSimilarProcesses.length} Reference(s)
            </button>
          </div>
        </div>
      )}

      {/* Similar Form Modal Overlay */}
      {showSimilarFormPanel && (
        <div className="absolute inset-0 bg-white/98 backdrop-blur-md z-20 flex flex-col shadow-[-10px_0_20px_rgba(0,0,0,0.05)] border-l border-gray-200">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-white">
            <div className="font-semibold text-gray-800 text-[13.5px]">
              Similar Form Reference
            </div>
            <button
              onClick={() => setShowSimilarFormPanel(false)}
              className="text-gray-400 hover:text-gray-700 hover:bg-gray-100 p-1.5 rounded transition-colors"
            >
              <X size={16} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-5 flex flex-col gap-5">
            {isLoadingSimilarForms ? (
              <div className="text-center text-gray-500 py-10 flex flex-col items-center justify-center gap-3">
                <RefreshCw
                  size={24}
                  className="animate-spin text-indigo-400 mx-auto"
                />
                <p className="text-sm font-medium">
                  Scanning database for similar forms...
                </p>
              </div>
            ) : (
              <>
                {similarForms.length > 0 ? (
                  <div>
                    <p className="text-[13px] font-medium text-gray-600 mb-3 block">
                      Matches found automatically:
                    </p>
                    <div className="flex flex-col gap-2.5">
                      {similarForms.map((p) => (
                        <div
                          key={p.id}
                          className={`p-3.5 border rounded-xl transition-all duration-200 ${p.approved ? "border-indigo-500 bg-indigo-50/50 shadow-sm" : "border-gray-200 bg-white hover:border-gray-300"}`}
                        >
                          <div className="flex justify-between items-start gap-4">
                            <div>
                              <div className="font-semibold text-[13px] text-gray-800 leading-tight">
                                {p.name}
                              </div>
                              <div className="text-[12px] text-gray-500 mt-1 line-clamp-2">
                                {p.description ||
                                  "No description available for this form."}
                              </div>
                            </div>
                            <button
                              onClick={() => {
                                const updated = similarForms.map((sim) =>
                                  sim.id === p.id
                                    ? { ...sim, approved: !sim.approved }
                                    : sim,
                                );
                                setSimilarForms(updated);
                                setSelectedSimilarForms(
                                  updated.filter((sim) => sim.approved),
                                );
                              }}
                              className={`flex-shrink-0 text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full transition-all duration-200 ${p.approved ? "bg-indigo-600 text-white border border-transparent shadow-sm" : "bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100 hover:text-gray-800"}`}
                            >
                              {p.approved ? "✓ Selected" : "Select"}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-[13px] text-gray-500 bg-orange-50/50 p-4 rounded-xl border border-orange-100 text-center flex flex-col items-center justify-center gap-2">
                    <Search size={20} className="text-orange-300" />
                    <p>
                      No similar forms found automatically.
                      <br />
                      Try searching manually below.
                    </p>
                  </div>
                )}

                <div className="border-t border-gray-100 pt-5">
                  <div className="text-[10.5px] font-semibold text-gray-500 uppercase tracking-wider mb-2.5">
                    Manual Directory Search
                  </div>
                  <div className="flex gap-2 mb-3">
                    <div className="relative flex-1">
                      <input
                        type="text"
                        value={formSearchTerm}
                        onChange={(e) => setFormSearchTerm(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && searchForms()}
                        placeholder="Search forms by name..."
                        className="w-full text-sm border border-gray-300 rounded-lg pl-3 pr-10 py-2 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none transition-all"
                      />
                      <button
                        onClick={searchForms}
                        className="absolute right-1 top-1 bottom-1 px-2.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors flex items-center justify-center"
                      >
                        <Search size={16} />
                      </button>
                    </div>
                  </div>
                  {formSearchResults.length > 0 && (
                    <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-xl bg-white shadow-sm ring-1 ring-black ring-opacity-5">
                      {formSearchResults.map((res) => (
                        <div
                          key={res.id}
                          className="px-3.5 py-2.5 border-b border-gray-100 last:border-0 hover:bg-indigo-50 cursor-pointer flex justify-between items-center group transition-colors"
                          onClick={() => {
                            if (
                              selectedSimilarForms.length < 3 &&
                              !selectedSimilarForms.find((s) => s.id === res.id)
                            ) {
                              setSelectedSimilarForms([
                                ...selectedSimilarForms,
                                { ...res, approved: true },
                              ]);
                              setFormSearchTerm("");
                              setFormSearchResults([]);
                            }
                          }}
                        >
                          <div>
                            <span className="font-medium text-gray-700 text-sm block">
                              {res.name}
                            </span>
                            {res.description && (
                              <span className="text-[11px] text-gray-400 block line-clamp-1 truncate pr-4">
                                {res.description}
                              </span>
                            )}
                          </div>
                          <span className="text-[11px] font-bold uppercase tracking-wider bg-white border border-gray-200 text-gray-500 px-2 py-1 rounded shadow-sm opacity-0 group-hover:opacity-100 group-hover:border-indigo-200 group-hover:text-indigo-600 transition-all flex-shrink-0">
                            Add +
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {selectedSimilarForms.length > 0 && (
                  <div className="bg-indigo-50/50 border border-indigo-100 rounded-xl p-3.5 shadow-sm mt-auto">
                    <div className="text-[10.5px] font-semibold text-indigo-800 uppercase tracking-wider mb-2.5 flex items-center justify-between">
                      <span>Selected References</span>
                      <span className="bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">
                        {selectedSimilarForms.length} / 3
                      </span>
                    </div>
                    <div className="flex flex-col gap-2">
                      {selectedSimilarForms.map((p) => (
                        <div
                          key={p.id}
                          className="flex justify-between items-center text-[13px] bg-white px-3 py-2 rounded-lg border border-indigo-100 shadow-sm group"
                        >
                          <span className="text-gray-700 truncate font-medium">
                            {p.name}
                          </span>
                          <button
                            onClick={() => {
                              setSelectedSimilarForms(
                                selectedSimilarForms.filter(
                                  (s) => s.id !== p.id,
                                ),
                              );
                              setSimilarForms(
                                similarForms.map((sim) =>
                                  sim.id === p.id
                                    ? { ...sim, approved: false }
                                    : sim,
                                ),
                              );
                            }}
                            className="text-gray-400 hover:text-red-500 hover:bg-red-50 p-1 rounded transition-colors"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="p-4 border-t border-gray-200 bg-white flex gap-3 justify-end shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.02)]">
            <button
              onClick={() => setShowSimilarFormPanel(false)}
              className="px-5 py-2 text-sm font-semibold text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-800 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={submitWithSimilarForms}
              disabled={selectedSimilarForms.length === 0}
              className="px-5 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:bg-gray-400 transition-colors shadow-sm whitespace-nowrap"
            >
              Use {selectedSimilarForms.length} Reference(s)
            </button>
          </div>
        </div>
      )}

      {/* Input Area */}
      <div className="px-4 py-3 bg-white border-t border-gray-200 z-10 shadow-[0_-4px_10px_-1px_rgba(0,0,0,0.05)]">
        <form onSubmit={handleSendMessage} className="relative">
          <div className="relative flex items-end gap-2 px-4 py-3 bg-white border border-gray-300 rounded-3xl shadow-sm hover:shadow-md focus-within:shadow-lg focus-within:border-indigo-400 transition-all">
            <textarea
              value={inputMessage}
              onChange={(e) => {
                setInputMessage(e.target.value);
                e.target.style.height = 'auto';
                e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              disabled={isLoading || isListening}
              placeholder={isListening ? "Listening..." : placeholderText}
              rows={1}
              className="flex-1 resize-none outline-none text-[14px] text-gray-800 placeholder-gray-400 bg-transparent max-h-[120px] overflow-y-auto disabled:text-gray-400"
              style={{ minHeight: '24px' }}
            />
            <div className="flex items-center gap-1 flex-shrink-0">
              {isSpeechSupported && (
                <button
                  type="button"
                  onClick={handleMicrophoneToggle}
                  disabled={isLoading}
                  title={isListening ? "Stop recording" : "Click to speak"}
                  className={`p-2 rounded-full transition-all ${
                    isListening
                      ? "bg-red-500 text-white hover:bg-red-600 animate-pulse"
                      : recordingState === 'error'
                      ? "bg-orange-100 text-orange-600 hover:bg-orange-200"
                      : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {isListening ? <MicOff size={18} /> : <Mic size={18} />}
                </button>
              )}
              <button
                type="submit"
                disabled={!inputMessage.trim() || isLoading}
                className={`p-2 rounded-full transition-all ${
                  inputMessage.trim() && !isLoading
                    ? `${primaryBg} text-white ${primaryHoverBg} shadow-md`
                    : "text-gray-300 cursor-not-allowed"
                }`}
              >
                <Send size={18} />
              </button>
            </div>
          </div>
        </form>
        <div className="mt-2.5 text-center px-4">
          {isListening && (
            <div className="text-[11px] font-semibold text-red-500 tracking-wide leading-tight animate-pulse">
              🎤 Listening... Speak now
            </div>
          )}
          {!isListening && speechError && (
            <div className="text-[11px] font-medium text-orange-500 tracking-wide leading-tight">
              {speechError}
            </div>
          )}
          {!isListening && !speechError && isProcessContext && (
            <div className="text-[11px] font-medium text-gray-400 tracking-wide leading-tight">
              Try: "Add approval step" &bull; "Optimize process" &bull; "Explain
              flow" &bull; "Add error handling"
            </div>
          )}
          {!isListening && !speechError && isFormContext && (
            <div className="text-[11px] font-medium text-gray-400 tracking-wide leading-tight">
              Try: "Add email field" &bull; "Improve layout" &bull; "Add
              validation" &bull; "Create contact form"
            </div>
          )}
          {!isListening && !speechError && hasNoContext && (
            <div className="text-[11px] font-medium text-gray-400 tracking-wide leading-tight">
              Try: "Add approval step" &bull; "Add email field" &bull; "Optimize
              process" &bull; "Improve layout"
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AiChatSidebar;
